import { useState, useEffect } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import { Checkbox, Input } from "@mui/material";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { theme } from "$utils/theme/bataillog";

import Text from "$components/Text";
import Button from "$components/Button";
import { publicIconPath } from "$utils/constants";
import { emailRegexp } from "$utils/utils";
import { PASS_REGEX, PASS_REGEX_ERROR_MESSAGE } from "./constants";

type Props = {
  passwordField?: boolean;
  stayConnected?: boolean;
  forgottenPassword?: boolean;
  connectionLabel?: string;
  callbackError?: string;
  newPasswordFields?: boolean;
  onSubmit: (data: any) => void;
  submitContainerClassName?: string;
  getIsValid?: (data: any) => void;
  getEmail?: (data: string) => void;
  emailMatch?: RegExp;
  disableForm?: boolean;
  setEmail?: (data: string) => void;
  setError?: (data: string) => void;
};

function ConnexionForm({
  onSubmit,
  passwordField = false,
  stayConnected = false,
  connectionLabel = "Se connecter",
  submitContainerClassName = "",
  callbackError = "",
  newPasswordFields = false,
  getIsValid = () => false,
  getEmail = () => false,
  emailMatch = emailRegexp,
  disableForm = false,
  setError = () => false,
}: Props) {
  const classes = useStyles();
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
  });
  const [eyeOn, setEyeOn] = useState<boolean>(false);

  const newPasssword = watch("newPasssword", "");
  const email = watch("email", "");

  const onSubmitHandler: SubmitHandler<any> = (data: any) => {
    onSubmit(data);
  };

  useEffect(() => {
    getIsValid(isValid);
  }, [getIsValid, isValid]);

  useEffect(() => {
    getEmail(email);
  }, [getEmail, email]);

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className={classes.form}>
      <div className={classes.fieldWithLabel}>
        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={{
            required: "Champ obligatoire",
            pattern: {
              value: emailMatch,
              message: "Veuillez saisir une adresse valide",
            },
          }}
          render={({ field }) => (
            <>
              {passwordField && (
                <Text className={classes.errorField}>{callbackError}</Text>
              )}
              <label htmlFor="email" className={classes.fieldLabel}>
                <Text>E-mail</Text>
              </label>
              <Input
                placeholder="Tapez votre e-mail"
                {...field}
                type="email"
                autoComplete="email mail username"
                name="email"
                className={classes.input}
                id="emailInput"
                onChange={(e) => {
                  field.onChange(e);
                  setValue("email", e.target.value);
                  setError("");
                }}
              />
              <Text className={classes.errorField}>
                {(errors.email &&
                  (errors.email.message as unknown as string)) ||
                  (!passwordField && callbackError)}
              </Text>
            </>
          )}
        />
      </div>
      {passwordField && (
        <div className={classes.fieldWithLabel}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              required: "Champ obligatoire",
              pattern: {
                value: PASS_REGEX,
                message: PASS_REGEX_ERROR_MESSAGE,
              },
            }}
            render={({ field }) => (
              <>
                <label htmlFor="email" className={classes.fieldLabel}>
                  <Text>Mot de passe</Text>
                </label>
                <Input
                  placeholder="Tapez votre mot de passe"
                  {...field}
                  type={eyeOn ? "text" : "password"}
                  className={classes.input}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("password", (e.target as HTMLInputElement).value);
                    setError("");
                  }}
                />
                <Text className={classes.errorField}>
                  {errors.password && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: errors.password.message as unknown as string,
                      }}
                    />
                  )}
                </Text>
                {eyeOn && (
                  <img
                    alt="eye-switch"
                    src={`${publicIconPath}/eye-off.svg`}
                    className={classes.EyeImg}
                    onClick={() => setEyeOn(false)}
                    onKeyDown={() => setEyeOn(false)}
                  />
                )}
                {!eyeOn && (
                  <img
                    alt="eye-switch"
                    src={`${publicIconPath}/eye-on.svg`}
                    className={classes.EyeImg}
                    onClick={() => setEyeOn(true)}
                    onKeyDown={() => setEyeOn(true)}
                  />
                )}
              </>
            )}
          />
        </div>
      )}
      {newPasswordFields && (
        <>
          <div className={classes.fieldWithLabel}>
            <Controller
              name="newPasssword"
              control={control}
              defaultValue=""
              rules={{
                required: "Champ obligatoire",
                pattern: {
                  value: PASS_REGEX,
                  message: PASS_REGEX_ERROR_MESSAGE,
                },
              }}
              render={({ field }) => (
                <>
                  <label htmlFor="newPasssword" className={classes.fieldLabel}>
                    <Text>Nouveau mot de passe</Text>
                  </label>
                  <Input
                    placeholder="Tapez votre mot de passe"
                    {...field}
                    type={eyeOn ? "text" : "password"}
                    className={classes.input}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue(
                        "newPassword",
                        (e.target as HTMLInputElement).value
                      );
                    }}
                    onPaste={(e) => {
                      field.onChange(e);
                      setValue(
                        "newPassword",
                        (e.target as HTMLInputElement).value
                      );
                    }}
                    error={Boolean(errors.newPasssword)}
                  />
                  <Text className={classes.errorField}>
                    {errors.newPasssword && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: errors.newPasssword
                            .message as unknown as string,
                        }}
                      />
                    )}
                  </Text>
                  {eyeOn && (
                    <img
                      alt="eye-switch"
                      src={`${publicIconPath}/eye-off.svg`}
                      className={classes.EyeImg}
                      onClick={() => setEyeOn(false)}
                      onKeyDown={() => setEyeOn(false)}
                    />
                  )}
                  {!eyeOn && (
                    <img
                      alt="eye-switch"
                      src={`${publicIconPath}/eye-on.svg`}
                      className={classes.EyeImg}
                      onClick={() => setEyeOn(true)}
                      onKeyDown={() => setEyeOn(true)}
                    />
                  )}
                </>
              )}
            />
          </div>
          <div className={classes.fieldWithLabel}>
            <Controller
              name="newPassswordCheck"
              control={control}
              defaultValue=""
              rules={{
                required: "Champ obligatoire",
                validate: {
                  value: (value) =>
                    value === newPasssword ||
                    "Les mots de passe ne sont pas identiques",
                },
              }}
              render={({ field }) => (
                <>
                  <label htmlFor="newPasssword" className={classes.fieldLabel}>
                    <Text>Confirmation du mot de passe</Text>
                  </label>
                  <Input
                    placeholder="Tapez votre mot de passe"
                    {...field}
                    type={eyeOn ? "text" : "password"}
                    className={classes.input}
                    onPaste={(e) => {
                      field.onChange(e);
                      setValue(
                        "newPasswordCheck",
                        (e.target as HTMLInputElement).value
                      );
                    }}
                    error={Boolean(errors.newPassswordCheck)}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue(
                        "newPasswordCheck",
                        (e.target as HTMLInputElement).value
                      );
                    }}
                  />
                  <Text className={classes.errorField}>
                    {errors.newPassswordCheck &&
                      (errors.newPassswordCheck.message as unknown as string)}
                  </Text>
                  {eyeOn && (
                    <img
                      alt="eye-switch"
                      src={`${publicIconPath}/eye-off.svg`}
                      className={classes.EyeImg}
                      onClick={() => setEyeOn(false)}
                      onKeyDown={() => setEyeOn(false)}
                    />
                  )}
                  {!eyeOn && (
                    <img
                      alt="eye-switch"
                      src={`${publicIconPath}/eye-on.svg`}
                      className={classes.EyeImg}
                      onClick={() => setEyeOn(true)}
                      onKeyDown={() => setEyeOn(true)}
                    />
                  )}
                </>
              )}
            />
          </div>
        </>
      )}
      {stayConnected && (
        <div className={classes.connectionOption}>
          <div>
            <Controller
              name="stay_connected"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  className={classes.checkBoxStay}
                  id="stay_connected"
                  color="primary"
                />
              )}
            />
            <label htmlFor="stay_connected">
              <Text>Rester connecté</Text>
            </label>
          </div>
          <Link to="/mot-de-passe" className={classes.passwordLink}>
            <Text type="link">Mot de passe oublié ?</Text>
          </Link>
        </div>
      )}
      <div
        className={clsx(classes.btnConnectContainer, submitContainerClassName)}
      >
        <Button
          type="submit"
          className={classes.btnConnect}
          disabled={Boolean(
            disableForm ||
            callbackError ||
            errors.newPasssword ||
            errors.newPassswordCheck ||
            (!newPasswordFields &&
              passwordField &&
              (!passwordField || !getValues("password"))) ||
            (newPasswordFields &&
              (!getValues("newPasssword") ||
                !getValues("newPassswordCheck"))) ||
            !email ||
            errors.email
          )}
        >
          {connectionLabel}
        </Button>
      </div>
    </form>
  );
}

const useStyles = createUseStyles(() => ({
  form: {
    flex: "0 1 auto",
    width: 424,
  },
  fieldWithLabel: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    margin: [15, 0],
  },
  input: {
    flex: 1,
    border: `1px solid ${theme.color.gray2}`,
    paddingLeft: 10,
    paddingRight: 10,
    "&:before": {
      display: "none",
    },
    "& input": {
      height: 29,
      fontSize: 15,
    },
  },
  fieldLabel: {
    margin: [15, 0],
  },
  EyeImg: {
    position: "absolute",
    top: 63,
    right: 14,
    cursor: "pointer",
  },
  connectionOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  checkBoxStay: {
    "&.Mui-checked": {
      color: `${theme.color.blueLight} !important`,
    },
    marginLeft: -13,
  },
  btnConnectContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    margin: [50, 0],
    [`@media (max-height: ${theme.media.mobileHeight}px)`]: {
      margin: [10, 0],
    },
  },
  btnConnect: {
    width: 182,
    height: 40,
  },
  passwordLink: {
    textDecorationColor: theme.color.gray5,
  },
  errorField: {
    minHeight: 20,
    color: theme.color.red,
    fontSize: 12,
  },
}));

export default ConnexionForm;
